import React from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { motion } from 'framer-motion'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Button, Typography } from '@material-ui/core'
import CyberpunkParticles from './CyberpunkParticles'
import CyberpunkGeometry from './CyberpunkGeometry'
import GlitchEffect from './GlitchEffect'
import { SimpleBurjKhalifa, SimpleGoldenGateBridge } from './SimpleLandmarks'
import cyberpunkStyles from './CyberpunkStyles'

const CyberpunkScene = () => {
  return (
    <>

      {/* Cyberpunk lighting setup */}
      <ambientLight intensity={0.3} color="#ff00ff"/>
      <pointLight position={[10, 8, 10]} intensity={1.2} color="#ff00ff"/>
      <pointLight position={[-10, 8, -10]} intensity={1.0} color="#00ffff"/>
      <pointLight position={[0, 12, 0]} intensity={0.8} color="#ff0080"/>
      {/* Additional stage lighting */}
      <pointLight position={[0, 2, 0]} intensity={0.5} color="#ffffff"/>

      {/* Cyberpunk particle system */}
      <CyberpunkParticles count={1200}/>

      {/* Landmarks sitting on the stage */}
      <SimpleGoldenGateBridge
        position={[-4, 0, 2]}
        rotationSpeed={0.015}
        scale={1}
      />

      <SimpleBurjKhalifa
        position={[4, 0, -2]}
        rotationSpeed={0.02}
        scale={0.8}
      />

      <CyberpunkGeometry
        position={[0, 0, -5]}
        geometry={<octahedronGeometry args={[1]}/>}
        rotationSpeed={0.025}
        scale={0.8}
      />

      <CyberpunkGeometry
        position={[-6, 0, -1]}
        geometry={<icosahedronGeometry args={[0.7]}/>}
        rotationSpeed={0.018}
        scale={1.1}
      />

      <CyberpunkGeometry
        position={[6, 0, 3]}
        geometry={<tetrahedronGeometry args={[0.9]}/>}
        rotationSpeed={0.022}
        scale={0.9}
      />

      {/* Glitch effect overlay */}
      <GlitchEffect/>

      {/* Camera controls with cyberpunk feel */}
      <OrbitControls
        enablePan={false}
        enableZoom={true}
        minDistance={8}
        maxDistance={20}
        maxPolarAngle={Math.PI / 2.2}
        minPolarAngle={Math.PI / 6}
        autoRotate
        autoRotateSpeed={0.2}
      />
    </>
  )
}

const useStyles = makeStyles(cyberpunkStyles)

const CyberpunkHero3D = ({ className }) => {
  const classes = useStyles()

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 2 }}
      style={cyberpunkStyles.cyberpunkContainer}
    >
      {/* System Performance Toggle moved to cyberspace page */}

      {/* Flowing background animation */}
      <Box style={cyberpunkStyles.flowingBackground}/>

      {/* Glitch overlay */}
      <Box style={cyberpunkStyles.glitchOverlay}/>

      {/* 3D Canvas */}
      <Box style={{ height: '100vh', width: '100%' }}>
        <Canvas
          camera={{ position: [0, 5, 12], fov: 75 }}
          style={{ background: 'transparent' }}
        >
          <CyberpunkScene/>
        </Canvas>
      </Box>


      {/* Hero content overlay */}
      <Box style={cyberpunkStyles.heroSection}>
        <Box style={{ textAlign: 'center', zIndex: 10, position: 'relative' }}>
          {/* Glitch title animation */}
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 1.5, ease: 'easeOut' }}
          >
            <Typography style={cyberpunkStyles.cyberpunkTitle}>
              STANLEY CHAN
            </Typography>
          </motion.div>

          {/* Subtitle with typewriter effect */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1, duration: 1.5, ease: 'easeOut' }}
          >
            <Typography style={cyberpunkStyles.cyberpunkSubtitle}>
              &gt; FULL_STACK_DEVELOPER.EXE
            </Typography>
            <Typography style={{
              ...cyberpunkStyles.cyberpunkSubtitle,
              fontSize: '1.1rem',
              marginTop: '0.5rem',
              color: '#ff0080'
            }}>
              &gt; CREATIVE_TECHNOLOGIST.SYS
            </Typography>
          </motion.div>

          {/* Cyberpunk action buttons */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.5, duration: 1.5, ease: 'easeOut' }}
            style={{ marginTop: '3rem', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}
          >
            <Button style={cyberpunkStyles.cyberpunkButton}>
              [INITIALIZE_PORTFOLIO]
            </Button>
            <Button style={{
              ...cyberpunkStyles.cyberpunkButton,
              borderColor: '#00ffff',
              color: '#00ffff'
            }}>
              [ACCESS_PROJECTS]
            </Button>
          </motion.div>

          {/* Terminal-style status text */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 1 }}
            style={{
              marginTop: '2rem',
              fontFamily: '"Share Tech Mono", monospace',
              fontSize: '0.9rem',
              color: '#666',
              letterSpacing: '0.1em'
            }}
          >
            <div style={{ color: '#00ff00' }}>
              &gt; SYSTEM_STATUS: ONLINE
            </div>
            <div style={{ color: '#ffff00', marginTop: '0.3rem' }}>
              &gt; NEURAL_LINK: ESTABLISHED
            </div>
            <div style={{ color: '#ff00ff', marginTop: '0.3rem' }}>
              &gt; READY_FOR_RECRUITMENT.PROTOCOL
            </div>
          </motion.div>
        </Box>
      </Box>
    </motion.div>
  )
}

export default CyberpunkHero3D
